import React, { ReactNode } from 'react';

interface FlyoutGridLayoutProps {
  children?: ReactNode;
}

interface FlyoutGridSectionProps {
  title: string;
  children?: ReactNode;
}

interface FlyoutGridRowProps {
  label: string;
  value?: ReactNode;
  children?: ReactNode;
  isLast?: boolean;
}

/**
 * Main grid layout container for flyout panes
 * Uses a 30%/70% column split with bordered cells
 */
export function FlyoutGridLayout({ children }: FlyoutGridLayoutProps) {
  return (
    <div className="border border-gray-200 dark:border-gray-700">
      <div className="grid" style={{ gridTemplateColumns: '30% 70%' }}>
        {children}
      </div>
    </div>
  );
}

/**
 * Section header for grouping related fields in the grid
 */
export function FlyoutGridSection({ title, children }: FlyoutGridSectionProps) {
  return (
    <>
      <div className="col-span-2 border-b border-gray-200 dark:border-gray-700 py-3 px-4 bg-gray-50 dark:bg-gray-800">
        <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
          {title}
        </h3>
      </div>
      {children}
    </>
  );
}

/**
 * Individual row in the grid layout with label and value
 * @param label - The field label (left column)
 * @param value - The field value (right column)
 * @param isLast - If true, removes bottom border (for last row in section)
 */
export function FlyoutGridRow({ label, value, children, isLast = false }: FlyoutGridRowProps) {
  const borderClass = isLast ? '' : 'border-b';
  const displayValue = children ?? value;

  return (
    <>
      <div className={`${borderClass} border-r border-gray-200 dark:border-gray-700 py-3 px-4`}>
        <div className="text-sm font-medium text-gray-900 dark:text-white">{label}</div>
      </div>
      <div className={`${borderClass} border-gray-200 dark:border-gray-700 py-3 px-4`}>
        <div className="text-sm text-gray-900 dark:text-white">{displayValue}</div>
      </div>
    </>
  );
}

/**
 * Example usage:
 *
 * <FlyoutGridLayout>
 *   <FlyoutGridSection title="USER INFORMATION">
 *     <FlyoutGridRow label="Name" value="John Doe" />
 *     <FlyoutGridRow label="Email" value="john@example.com" />
 *     <FlyoutGridRow label="Role" value="Admin" isLast />
 *   </FlyoutGridSection>
 *
 *   <FlyoutGridSection title="ACCOUNT DETAILS">
 *     <FlyoutGridRow label="Created" value="Jan 1, 2024" />
 *     <FlyoutGridRow label="Status" value="Active" isLast />
 *   </FlyoutGridSection>
 * </FlyoutGridLayout>
 */



